/**
 * MonthlyAllowanceBanner — usage-allowance warning notice.
 *
 * signup-billing-mvp Phase 6 Session 3 (2026-05-13); threshold lowered to
 * 90% + read-only UX gate added 2026-05-14.
 *
 * Self-gates on the AND-shape `usage_limiting_enabled && pct >= 0.90`
 * (research § 11 risk #9 + root CLAUDE.md footgun #14 — collapsing to a
 * single-condition gate would mis-show the banner to capture-only users).
 * Admin / unapproved / no-balance users render `null`.
 *
 * Copy switches at the 100% boundary: 90-99% shows "APPROACHING LIMIT" with
 * the current % rendered; 100%+ shows "ALLOWANCE REACHED" with the reset
 * date. Same threshold drives `useBillingReadOnly()` which disables Builder
 * + Model View edits.
 *
 * Dismissibility is in-memory only (Pattern B per research § 11 risk #3) —
 * dismissing hides the component for the current mount; the banner re-shows
 * on the next `PAYMENT_REQUIRED` SSE signal because `useStreaming.sendMessage`
 * clears the context at run-start (`setPaymentRequired(null)`) and the SSE
 * error case pushes the new signal back in.
 *
 * Composed inline per the `<SharedDealBanner>` / `<ShareForkBanner>` /
 * `<ToolErrorCallout>` recipe (research § 4.10) — warn-tinted triplet
 * `bg-warn-dim border border-warn/15 text-warn` (research § 4.11). No new
 * primitive — research § 11 risk #2 confirms inline composition matches the
 * 3-precedent shape.
 *
 * NO `$` figures. NO AI-vendor names. NO top-up CTA — verified by token-discipline grep
 * (research § 11 risk #4).
 */

import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { CalendarClock, X } from 'lucide-react';
import { useAuth } from '@/hooks/use-auth';
import { useApprovalStatus } from '@/hooks/use-approval-status';
import { useBillingUsage, asBillingUsageRow } from '@/hooks/use-billing-usage';
import { useStreamingPaymentRequired } from '@/hooks/use-streaming-payment-required';
import { posthog } from '@/lib/posthog';

export function MonthlyAllowanceBanner() {
  const { isAdmin } = useAuth();
  const { isApproved } = useApprovalStatus();
  const { data } = useBillingUsage();
  const row = asBillingUsageRow(data);
  const { paymentRequired, dismiss } = useStreamingPaymentRequired();
  const [locallyDismissed, setLocallyDismissed] = useState(false);

  // NEW Phase 3 § 3.4 — PostHog product event: payment-required banner seen.
  // StrictMode ref-dedupe: fires exactly once per real mount of the banner.
  // The visible flag is derived from the gate conditions evaluated below;
  // the effect reads it directly to avoid stale-closure problems.
  const seenFiredRef = useRef(false);
  const isBannerVisible =
    !isAdmin &&
    isApproved &&
    !!row &&
    row.usage_limiting_enabled &&
    row.pct >= 0.9 &&
    !locallyDismissed;
  useEffect(() => {
    if (!isBannerVisible) return;
    if (seenFiredRef.current) return; // StrictMode double-mount guard
    seenFiredRef.current = true;
    posthog.capture('payment_required_seen', {});
  }, [isBannerVisible]);

  // Re-show the banner whenever a fresh `PAYMENT_REQUIRED` signal arrives.
  // `useStreamingV2` clears the context at run-start, so a non-null value
  // here means a NEW gate trip has fired since the last dismissal.
  useEffect(() => {
    if (paymentRequired) setLocallyDismissed(false);
  }, [paymentRequired]);

  // Self-gate. AND-gate is load-bearing.
  if (!isBannerVisible || !row) return null;

  const resetDate = new Date(row.period_end).toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
  const displayPct = Math.round(row.pct * 100);
  const atOrOverCap = row.pct >= 1.0;

  return (
    <div
      data-testid="monthly-allowance-banner"
      role="status"
      aria-live="polite"
      data-state={atOrOverCap ? 'reached' : 'approaching'}
      className={clsx(
        'flex items-center justify-between gap-4 px-5 py-2.5',
        'bg-warn-dim border border-warn/15 rounded-[3px]',
        'text-warn shrink-0'
      )}
    >
      <div className="flex items-start gap-2.5 min-w-0">
        <CalendarClock size={14} className="text-warn shrink-0 mt-[3px]" />
        <div className="flex flex-col gap-0.5 min-w-0">
          <span className="text-sm font-body text-text-bright truncate">
            {atOrOverCap ? (
              <>
                <span className="font-mono text-warn">
                  MONTHLY ALLOWANCE REACHED
                </span>
                <span className="text-text-muted mx-2">·</span>
                <span className="text-text-muted">
                  New runs are paused until your allowance resets on{' '}
                </span>
                <span className="font-mono text-text-bright">{resetDate}</span>
              </>
            ) : (
              <>
                <span className="font-mono text-warn">
                  APPROACHING MONTHLY ALLOWANCE
                </span>
                <span className="text-text-muted mx-2">·</span>
                <span className="font-mono text-text-bright">
                  {displayPct}%
                </span>
                <span className="text-text-muted">
                  {' '}
                  used &mdash; allowance resets on{' '}
                </span>
                <span className="font-mono text-text-bright">{resetDate}</span>
              </>
            )}
          </span>
          <span
            data-testid="overshoot-disclosure"
            className="font-mono text-[11px] text-text-muted"
          >
            A run already underway can finish past your monthly allowance.
          </span>
        </div>
      </div>
      <button
        type="button"
        onClick={() => {
          setLocallyDismissed(true);
          dismiss();
        }}
        aria-label="Dismiss"
        data-testid="monthly-allowance-banner-dismiss"
        className="shrink-0 text-warn hover:text-text-bright transition-colors"
      >
        <X size={14} />
      </button>
    </div>
  );
}
